import React, { useState, useEffect } from 'react';
import './LegalDisclaimer.css';

const STORAGE_KEY = 'afterlife-bot-legal-accepted';

export const DISCLAIMER_TEXT = (
  <>
    This chatbot uses an AI-generated avatar of historical figures to provide interactive,
    conversational content. The answers are generated by artificial intelligence based on
    historical data and not by a human. While we strive for accuracy, the information provided
    may be incorrect, incomplete, or inaccurate. This tool is for entertainment and educational
    purposes only. Do not rely on this simulation for historical research, professional advice,
    or decision-making. By engaging with this chatbot, you accept all risks associated with its
    use and acknowledge that you are not talking to a real person.
  </>
);

/**
 * LegalDisclaimer – small footer and/or one-time "I Agree" modal.
 * @param {Object} props
 * @param {'modal' | 'footer' | 'both'} [props.variant='both'] - 'modal' = one-time modal only, 'footer' = footer only, 'both' = modal on first visit then footer
 * @param {boolean} [props.showFooterAfterAccept=true] - When variant is 'both', show footer after user accepts modal
 */
function LegalDisclaimer({ variant = 'both', showFooterAfterAccept = true }) {
  const [accepted, setAccepted] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [footerExpanded, setFooterExpanded] = useState(false);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      setAccepted(stored === 'true');
      if (variant === 'modal' || variant === 'both') {
        setModalVisible(stored !== 'true');
      }
    } catch (e) {
      setModalVisible(variant === 'modal' || variant === 'both');
    }
  }, [variant]);

  const handleAccept = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, 'true');
    } catch (e) {}
    setAccepted(true);
    setModalVisible(false);
  };

  const showFooter =
    variant === 'footer' || (variant === 'both' && showFooterAfterAccept && accepted);

  return (
    <>
      {/* One-time modal */}
      {modalVisible && (variant === 'modal' || variant === 'both') && (
        <div className="legal-disclaimer-overlay" role="dialog" aria-modal="true" aria-labelledby="legal-disclaimer-title">
          <div className="legal-disclaimer-modal">
            <h2 id="legal-disclaimer-title" className="legal-disclaimer-modal__title">
              Legal disclaimer
            </h2>
            <p className="legal-disclaimer-modal__text">{DISCLAIMER_TEXT}</p>
            <button
              type="button"
              onClick={handleAccept}
              className="legal-disclaimer-modal__button"
              aria-label="I agree to the disclaimer"
            >
              I Agree
            </button>
          </div>
        </div>
      )}

      {/* Small non-intrusive footer */}
      {showFooter && (
        <footer className="legal-disclaimer-footer">
          <div className="legal-disclaimer-footer__row">
            <button
              type="button"
              onClick={() => setFooterExpanded((e) => !e)}
              className="legal-disclaimer-footer__trigger"
              aria-expanded={footerExpanded}
              aria-label={footerExpanded ? 'Hide legal disclaimer' : 'Show legal disclaimer'}
            >
              Legal
            </button>
            <a href="/terms-of-service" className="legal-disclaimer-footer__link">
              Terms of Service
            </a>
            <a href="/privacy-policy" className="legal-disclaimer-footer__link">
              Privacy Policy
            </a>
          </div>
          {footerExpanded && (
            <p className="legal-disclaimer-footer__text">{DISCLAIMER_TEXT}</p>
          )}
        </footer>
      )}
    </>
  );
}

export default LegalDisclaimer;
